/**
 * Chunked upload client.
 *
 * Cloudflare fronts the deployed instances and rejects request bodies over
 * roughly 100 MiB with its own HTML error page before the request reaches the
 * server. Files above the threshold are therefore sliced in the browser and
 * sent as bounded chunks, then assembled server-side into the same workspace
 * asset a direct upload would produce.
 */

import {
  api,
  CHUNKED_UPLOAD_THRESHOLD_BYTES,
  UPLOAD_CHUNK_SIZE_BYTES,
  type WorkspaceAssetUploadResult,
} from './api';

const MAX_CHUNK_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 500;

/** Statuses that will not improve on retry. */
const FATAL_CHUNK_STATUSES = new Set([401, 403, 409, 410, 413]);

export function needsChunkedUpload(file: File): boolean {
  return file.size > CHUNKED_UPLOAD_THRESHOLD_BYTES;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function putChunkWithRetry(
  sessionId: string,
  chunkIndex: number,
  blob: Blob
): Promise<void> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= MAX_CHUNK_ATTEMPTS; attempt += 1) {
    try {
      await api.workspace.assets.uploads.putChunk(sessionId, chunkIndex, blob);
      return;
    } catch (error) {
      lastError = error;
      const status = (error as { status?: number }).status;
      if (typeof status === 'number' && FATAL_CHUNK_STATUSES.has(status)) throw error;
      if (attempt < MAX_CHUNK_ATTEMPTS) {
        await delay(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
      }
    }
  }

  throw lastError;
}

/**
 * Uploads one file through a chunked session. Retries are scoped to this call:
 * closing the tab abandons the session, which the server expires and cleans up.
 */
export async function uploadFileInChunks(
  file: File,
  destination: string,
  onProgress?: (fraction: number) => void
): Promise<WorkspaceAssetUploadResult> {
  const { session } = await api.workspace.assets.uploads.createSession({
    filename: file.name,
    totalSizeBytes: file.size,
    chunkSizeBytes: UPLOAD_CHUNK_SIZE_BYTES,
    destination,
    mimeType: file.type || null,
  });

  try {
    const alreadyUploaded = new Set(session.uploadedChunkIndexes);

    for (let index = 0; index < session.chunkCount; index += 1) {
      if (!alreadyUploaded.has(index)) {
        const start = index * session.chunkSizeBytes;
        const end = Math.min(start + session.chunkSizeBytes, file.size);
        await putChunkWithRetry(session.id, index, file.slice(start, end));
      }
      onProgress?.((index + 1) / session.chunkCount);
    }

    return await api.workspace.assets.uploads.complete(session.id);
  } catch (error) {
    await api.workspace.assets.uploads.cancel(session.id).catch(() => {});
    throw error;
  }
}
